'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useActivities, Activity } from '@/lib/hooks/useActivities';
import { useUnifiedCart } from '@/lib/unified-cart';
import { lodges } from '@/lib/data/lodges';
import { isActivityDateBlocked, getEffectiveBlockedDates, hasSelectableDateInRange } from '@/lib/booking/activity-availability';
import styles from './ActivityCrossSell.module.css';

/* ──────────────────────────────────────────────
   TYPES
   ────────────────────────────────────────────── */

interface ActivityCrossSellProps {
  locale: 'en' | 'es' | 'pt';
  lodgeCheckIn?: string;    // YYYY-MM-DD (backward compat)
  lodgeCheckOut?: string;   // YYYY-MM-DD (backward compat)
  lodgeDateRanges?: Array<{ checkIn: string; checkOut: string }>;  // Multiple date ranges
  guests?: number;
  locationId?: string;
  locationIds?: string[];   // Multiple location IDs
  onSkip?: () => void;      // Called when user clicks "Skip"
  className?: string;
}

/* ──────────────────────────────────────────────
   I18N — migrated to next-intl (namespace: activityCrossSell)
   ────────────────────────────────────────────── */

/* ──────────────────────────────────────────────
   INLINE SVG ICONS
   ────────────────────────────────────────────── */

function CheckIcon({ size = 15 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}

/* ──────────────────────────────────────────────
   MAIN COMPONENT
   ────────────────────────────────────────────── */

export default function ActivityCrossSell({
  locale,
  lodgeCheckIn,
  lodgeCheckOut,
  lodgeDateRanges,
  guests = 2,
  locationId,
  locationIds,
  onSkip,
  className,
}: ActivityCrossSellProps) {
  const t = useTranslations('activityCrossSell');
  const { activities, loading } = useActivities();
  const { activityItems, addActivityItem, removeItem } = useUnifiedCart();

  // --- Filter activities by location (supports multiple locations) ---
  const filteredActivities = useMemo(() => {
    const ids = locationIds && locationIds.length > 0
      ? locationIds
      : (locationId ? [locationId] : []);

    if (ids.length === 0 || activities.length === 0) return activities;

    // Union of activities allowed in ANY location
    const allowedIds = new Set<string>();
    for (const locId of ids) {
      const lodge = lodges.find(l => l.id === locId);
      if (lodge) {
        for (const a of lodge.activities) {
          allowedIds.add(a.adventureId);
        }
      }
    }

    return allowedIds.size > 0 ? activities.filter(a => allowedIds.has(a.id)) : activities;
  }, [activities, locationId, locationIds]);

  // --- Local state ---
  const [recentlyAdded, setRecentlyAdded] = useState<string | null>(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [isAtScrollEnd, setIsAtScrollEnd] = useState(false);
  const [isAtScrollStart, setIsAtScrollStart] = useState(true);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(true);

  // --- Modal state ---
  const [modalActivity, setModalActivity] = useState<Activity | null>(null);
  const [modalDates, setModalDates] = useState<string[]>([]);
  const [modalGuests, setModalGuests] = useState(guests);
  const [modalClosing, setModalClosing] = useState(false);
  const [dateInputKey, setDateInputKey] = useState(0);

  // --- Refs ---
  const carouselRef = useRef<HTMLDivElement>(null);
  const recentlyAddedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Cleanup timers ---
  useEffect(() => {
    return () => {
      if (recentlyAddedTimer.current) clearTimeout(recentlyAddedTimer.current);
    };
  }, []);

  // --- Auto-dismiss swipe hint after 4s ---
  useEffect(() => {
    const timer = setTimeout(() => setShowSwipeHint(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  // --- Body scroll lock when modal is open ---
  useEffect(() => {
    if (modalActivity && !modalClosing) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalActivity, modalClosing]);

  // --- Available dates from lodge check-in/out ---
  // --- Available dates from ALL lodge date ranges (not just first) ---
  const availableDates = useMemo(() => {
    // Build list of ranges: use lodgeDateRanges if available, else fallback to single range
    const ranges = lodgeDateRanges && lodgeDateRanges.length > 0
      ? lodgeDateRanges
      : (lodgeCheckIn && lodgeCheckOut ? [{ checkIn: lodgeCheckIn, checkOut: lodgeCheckOut }] : []);

    if (ranges.length === 0) return [];

    const dateSet = new Set<string>();
    const dates: { date: string; label: string; dayName: string }[] = [];
    const localeStr = locale === 'es' ? 'es-CL' : locale === 'pt' ? 'pt-BR' : 'en-US';

    for (const range of ranges) {
      const start = new Date(range.checkIn + 'T12:00:00');
      const end = new Date(range.checkOut + 'T12:00:00');
      while (start < end) {
        const dateStr = start.toISOString().split('T')[0];
        if (!dateSet.has(dateStr)) {
          dateSet.add(dateStr);
          dates.push({
            date: dateStr,
            label: start.toLocaleDateString(localeStr, { weekday: 'short', month: 'short', day: 'numeric' }),
            dayName: start.toLocaleDateString(localeStr, { weekday: 'long' }),
          });
        }
        start.setDate(start.getDate() + 1);
      }
    }

    // Sort chronologically (in case ranges are not in order)
    dates.sort((a, b) => a.date.localeCompare(b.date));
    return dates;
  }, [lodgeDateRanges, lodgeCheckIn, lodgeCheckOut, locale]);

  // --- Activities worth OFFERING for this stay ---
  // Drop any activity with no selectable date inside the lodge range: don't
  // recommend something the guest can't book on any of their nights. With no
  // stay range (availableDates empty) we don't filter — the modal falls back
  // to a free date picker.
  const offerableActivities = useMemo(() => {
    if (availableDates.length === 0) return filteredActivities;
    const stayDates = availableDates.map((d) => d.date);
    return filteredActivities.filter((a) => hasSelectableDateInRange(a, stayDates));
  }, [filteredActivities, availableDates]);

  // --- Dates blocked for the activity currently open in the modal ---
  // Parity with the booking validator (functions/src/utils/availability.ts):
  // transport is never blocked; everything else uses the activity's blockedDates.
  // Without this the cross-sell would offer dates the backend rejects with
  // NOT_AVAILABLE ("... is not available on <date> (date is blocked)").
  const modalBlockedSet = useMemo(
    () => new Set(modalActivity ? getEffectiveBlockedDates(modalActivity) : []),
    [modalActivity]
  );

  // Lodge-range dates that are actually selectable for the modal activity.
  const selectableDates = useMemo(
    () => availableDates.filter((d) => !modalBlockedSet.has(d.date)),
    [availableDates, modalBlockedSet]
  );

  // True when the stay has dates but none are bookable for this activity.
  const allDatesBlocked = availableDates.length > 0 && selectableDates.length === 0;

  // --- Build set of added activity IDs from the unified cart ---
  const addedActivityIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of activityItems) {
      ids.add(item.activityId);
    }
    return ids;
  }, [activityItems]);

  // --- Carousel scroll tracking ---
  const handleCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;

    setNeedsScroll(el.scrollWidth > el.clientWidth + 10);

    const scrollLeft = el.scrollLeft;
    const cardEl = el.querySelector(`.${styles.activityCard}`) as HTMLElement | null;
    const cardWidth = cardEl?.clientWidth || 260;
    const gap = 16;
    const index = Math.round(scrollLeft / (cardWidth + gap));
    setActiveCardIndex(Math.min(index, offerableActivities.length - 1));

    const atEnd = scrollLeft + el.clientWidth >= el.scrollWidth - 10;
    setIsAtScrollEnd(atEnd);

    const atStart = scrollLeft <= 10;
    setIsAtScrollStart(atStart);
  }, [offerableActivities.length]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleCarouselScroll, { passive: true });
    handleCarouselScroll();
    const ro = new ResizeObserver(() => handleCarouselScroll());
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', handleCarouselScroll);
      ro.disconnect();
    };
  }, [handleCarouselScroll]);

  // --- Scroll to specific card (dot click) ---
  const scrollToCard = useCallback((index: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const cardEl = el.querySelector(`.${styles.activityCard}`) as HTMLElement | null;
    const cardWidth = cardEl?.clientWidth || 260;
    const gap = 16;
    el.scrollTo({ left: index * (cardWidth + gap), behavior: 'smooth' });
  }, []);

  // --- Scroll carousel by arrow ---
  const scrollCarousel = useCallback((direction: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = 280 + 16;
    el.scrollBy({ left: direction === 'right' ? cardWidth : -cardWidth, behavior: 'smooth' });
  }, []);

  // --- Open modal for an activity ---
  const openModal = useCallback((activity: Activity) => {
    // Check if already in cart — pre-populate if so (dropping any now-blocked dates)
    const existingItems = activityItems.filter(item => item.activityId === activity.id);
    const blocked = new Set(getEffectiveBlockedDates(activity));
    setModalActivity(activity);
    setModalDates(
      existingItems.length > 0
        ? existingItems.map(item => item.date).filter(d => !blocked.has(d))
        : []
    );
    setModalGuests(existingItems.length > 0 ? existingItems[0].adults : guests);
    setModalClosing(false);
  }, [activityItems, guests]);

  // --- Close modal with animation ---
  const closeModal = useCallback(() => {
    setModalClosing(true);
    setTimeout(() => {
      setModalActivity(null);
      setModalClosing(false);
    }, 250);
  }, []);

  // --- Confirm modal: add/update activity items in cart ---
  const confirmModal = useCallback(() => {
    if (!modalActivity || modalDates.length === 0) return;

    // Defense in depth: never add a date the backend would reject as blocked.
    const blocked = new Set(getEffectiveBlockedDates(modalActivity));
    const datesToAdd = modalDates.filter(d => !blocked.has(d));
    if (datesToAdd.length === 0) return;

    // 1. Remove all existing cart items for this activity
    const existingItems = activityItems.filter(item => item.activityId === modalActivity.id);
    for (const item of existingItems) {
      removeItem(item.cartItemId);
    }

    // 2. Add one cart item per selected date
    const pricePerPerson = modalActivity.basePrice.amount;
    for (const date of datesToAdd) {
      addActivityItem({
        itemType: 'activity',
        activityId: modalActivity.id,
        activitySlug: modalActivity.slug,
        activityName: modalActivity.name,
        activityImage: modalActivity.heroImage || '',
        date,
        persons: modalGuests,
        pricePerPerson,
        adults: modalGuests,
        children: 0,
        pricePerAdult: pricePerPerson,
        pricePerChild: pricePerPerson,
        subtotalUSD: pricePerPerson * modalGuests,
      });
    }

    // "Recently added" animation
    setRecentlyAdded(modalActivity.id);
    if (recentlyAddedTimer.current) clearTimeout(recentlyAddedTimer.current);
    recentlyAddedTimer.current = setTimeout(() => setRecentlyAdded(null), 1200);

    closeModal();
  }, [modalActivity, modalDates, modalGuests, activityItems, addActivityItem, removeItem, closeModal]);

  // --- Toggle date in modal ---
  const toggleModalDate = useCallback((date: string) => {
    if (modalBlockedSet.has(date)) return; // blocked dates are not selectable
    setModalDates(prev =>
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  }, [modalBlockedSet]);

  // --- Select all / deselect all dates ---
  const toggleAllDates = useCallback(() => {
    const allDates = selectableDates.map(d => d.date);
    setModalDates(prev => (allDates.length > 0 && prev.length === allDates.length ? [] : allDates));
  }, [selectableDates]);

  // --- Modal price calculation ---
  const modalTotal = useMemo(() => {
    if (!modalActivity) return 0;
    return modalActivity.basePrice.amount * modalGuests * modalDates.length;
  }, [modalActivity, modalGuests, modalDates]);

  // --- Compute discount info ---
  const addedCount = addedActivityIds.size;
  const hasBundleDiscount = addedCount >= 2;
  const activitiesSubtotal = activityItems.reduce((sum, item) => sum + item.subtotalUSD, 0);
  const discountAmount = hasBundleDiscount ? Math.round(activitiesSubtotal * 0.05) : 0;

  // --- Loading skeleton ---
  if (loading) {
    return (
      <div className={`${styles.wrapper} ${className || ''}`}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{t('title')}</h3>
        </div>
        <p className={styles.subtitle}>{t('subtitle')}</p>
        <div className={styles.skeletonCarousel}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.skeletonCard}>
              <div className={styles.skeletonImage} />
              <div className={styles.skeletonBody}>
                <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
                <div className={styles.skeletonFooter}>
                  <div className={styles.skeletonPrice} />
                  <div className={styles.skeletonBtn} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // --- No activities available ---
  if (!offerableActivities || offerableActivities.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.wrapper} ${className || ''}`}>
      {/* ── HEADER ── */}
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>{t('title')}</h3>
        <div className={styles.socialProofPill}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" strokeLinecap="round" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" strokeLinecap="round" />
          </svg>
          <span>{t('socialProof')}</span>
        </div>
      </div>

      <p className={styles.subtitle}>{t('subtitle')}</p>

      {/* ── CAROUSEL ── */}
      <div className={styles.carouselWrapper}>
        {/* Left arrow (only when content overflows) */}
        {needsScroll && (
          <button
            className={`${styles.carouselArrow} ${styles.carouselArrowLeft}`}
            onClick={() => scrollCarousel('left')}
            aria-label={t('scrollLeft')}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {/* Cards */}
        <div className={styles.carousel} ref={carouselRef}>
          {offerableActivities.map((activity, idx) => {
            const isAdded = addedActivityIds.has(activity.id);
            const justAdded = recentlyAdded === activity.id;

            return (
              <div
                key={activity.id}
                className={`${styles.activityCard} ${isAdded ? styles.activityCardAdded : ''} ${justAdded ? styles.activityCardPop : ''}`}
                style={{ animationDelay: justAdded ? '0ms' : `${idx * 80 + 200}ms` }}
              >
                {/* Card image */}
                <div className={styles.cardImage}>
                  {activity.heroImage && (
                    <Image
                      src={activity.heroImage}
                      alt={activity.name}
                      fill
                      style={{ objectFit: 'cover' }}
                      sizes="(max-width: 640px) 45vw, 240px"
                      unoptimized
                    />
                  )}
                  <div className={styles.cardImageOverlay} />
                  <span className={styles.cardDuration}>
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" strokeLinecap="round" />
                    </svg>
                    {t('durationHours', { hours: activity.durationHours })}
                  </span>
                </div>

                {/* Card body */}
                <div className={styles.cardBody}>
                  <div className={styles.cardTitleRow}>
                    <h4 className={styles.cardTitle}>{activity.name}</h4>
                    <span className={styles.cardPriceMobile}>
                      ${activity.basePrice.amount.toLocaleString()}
                    </span>
                  </div>
                  {activity.shortDescription && (
                    <p className={styles.cardDescription}>{activity.shortDescription}</p>
                  )}

                  <div className={styles.cardFooter}>
                    <div className={styles.cardPrice}>
                      <span className={styles.cardPriceAmount}>
                        ${activity.basePrice.amount.toLocaleString()}
                      </span>
                      <span className={styles.cardPricePer}>{t('perPerson')}</span>
                    </div>
                    <button
                      className={`${styles.addBtn} ${isAdded ? styles.addBtnAdded : ''}`}
                      onClick={() => openModal(activity)}
                      aria-label={isAdded
                        ? `${t('edit')} ${activity.name}`
                        : `${t('add')} ${activity.name}`
                      }
                    >
                      {isAdded ? (
                        <>
                          <CheckIcon size={15} />
                          <span>{t('added')}</span>
                        </>
                      ) : (
                        <>
                          <PlusIcon />
                          <span>{t('add')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Added border glow */}
                {isAdded && <div className={styles.addedGlow} />}
              </div>
            );
          })}
        </div>

        {/* Right arrow (only when content overflows) */}
        {needsScroll && (
          <button
            className={`${styles.carouselArrow} ${styles.carouselArrowRight}`}
            onClick={() => scrollCarousel('right')}
            aria-label={t('scrollRight')}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {/* Fade gradients (only when content overflows) */}
        {needsScroll && (
          <>
            <div className={`${styles.carouselFadeLeft} ${isAtScrollStart ? styles.carouselFadeLeftHidden : ''}`} />
            <div className={`${styles.carouselFadeRight} ${isAtScrollEnd ? styles.carouselFadeRightHidden : ''}`} />
          </>
        )}
      </div>

      {/* ── DOT INDICATORS (only when content overflows) ── */}
      {needsScroll && (
        <div className={styles.carouselDots}>
          {offerableActivities.map((activity, i) => (
            <button
              key={activity.id}
              className={`${styles.carouselDot} ${i === activeCardIndex ? styles.carouselDotActive : ''}`}
              onClick={() => scrollToCard(i)}
              aria-label={`Go to ${activity.name}`}
            />
          ))}
        </div>
      )}

      {/* ── SWIPE HINT (only when content overflows) ── */}
      {needsScroll && (
        <div className={`${styles.swipeHint} ${!showSwipeHint ? styles.swipeHintHidden : ''}`}>
          <span className={styles.swipeHintIcon}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {t('swipeHint')}
        </div>
      )}

      {/* NUDGE BANNER removed — cluttered mobile layout */}

      {hasBundleDiscount && (
        <div className={styles.savingsBanner}>
          {t('savings', { amount: discountAmount })}
        </div>
      )}

      {/* ── SKIP LINK ── */}
      {onSkip && (
        <div className={styles.skipRow}>
          <button className={styles.skipLink} onClick={onSkip}>
            {t('skip')}
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}

      {/* ── ACTIVITY DATE/GUEST MODAL ── */}
      {modalActivity && (
        <div
          className={`${styles.modalBackdrop} ${modalClosing ? styles.modalBackdropClosing : ''}`}
          onClick={closeModal}
        >
          <div
            className={`${styles.modalContainer} ${modalClosing ? styles.modalContainerClosing : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button className={styles.modalClose} onClick={closeModal} aria-label={t('closeModal')}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>

            {/* Header: image + activity info */}
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderImage}>
                {modalActivity.heroImage && (
                  <Image
                    src={modalActivity.heroImage}
                    alt={modalActivity.name}
                    fill
                    style={{ objectFit: 'cover' }}
                    sizes="420px"
                    unoptimized
                  />
                )}
                <div className={styles.modalHeaderOverlay} />
                <div className={styles.modalHeaderInfo}>
                  <h3 className={styles.modalHeaderTitle}>{modalActivity.name}</h3>
                  <span className={styles.modalHeaderPrice}>
                    ${modalActivity.basePrice.amount.toLocaleString()} <span className={styles.modalHeaderPricePer}>{t('perPersonPerDay')}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Dates section */}
            <div className={styles.modalBody}>
              <div className={styles.modalSection}>
                <div className={styles.modalSectionHeader}>
                  <h4 className={styles.modalSectionTitle}>{t('selectDates')}</h4>
                  {selectableDates.length > 0 && (
                    <button className={styles.modalSelectAll} onClick={toggleAllDates}>
                      {modalDates.length === selectableDates.length ? t('deselectAll') : t('selectAll')}
                    </button>
                  )}
                </div>
                {allDatesBlocked && (
                  <p className={styles.modalDateNotice}>{t('noDatesForActivity')}</p>
                )}
                {availableDates.length > 0 ? (
                  <div className={styles.modalDateList}>
                    {availableDates.map((d) => {
                      const isBlocked = modalBlockedSet.has(d.date);
                      const isSelected = !isBlocked && modalDates.includes(d.date);
                      return (
                        <button
                          key={d.date}
                          type="button"
                          className={`${styles.modalDateRow} ${isSelected ? styles.modalDateRowSelected : ''} ${isBlocked ? styles.modalDateRowDisabled : ''}`}
                          onClick={() => toggleModalDate(d.date)}
                          disabled={isBlocked}
                          aria-disabled={isBlocked}
                        >
                          <span className={`${styles.modalDateCheck} ${isSelected ? styles.modalDateCheckActive : ''}`}>
                            {isSelected && (
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3">
                                <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </span>
                          <div className={styles.modalDateInfo}>
                            <span className={styles.modalDateDay}>{d.dayName}</span>
                            <span className={styles.modalDateLabel}>{d.label}</span>
                          </div>
                          {isBlocked ? (
                            <span className={styles.modalDateUnavailable}>{t('dateUnavailable')}</span>
                          ) : (
                            <span className={styles.modalDateIcon}>
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <path d="M16 2v4M8 2v4M3 10h18" />
                              </svg>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.modalDateFallback}>
                    {modalDates.length > 0 && (
                      <div className={styles.modalDateChips}>
                        {modalDates.map(d => (
                          <div key={d} className={styles.modalDateChip}>
                            <span>{new Date(d + 'T12:00:00').toLocaleDateString(
                              locale === 'es' ? 'es-CL' : locale === 'pt' ? 'pt-BR' : 'en-US',
                              { month: 'short', day: 'numeric', year: 'numeric' }
                            )}</span>
                            <button
                              onClick={() => toggleModalDate(d)}
                              className={styles.modalDateChipRemove}
                            >×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <input
                      key={dateInputKey}
                      type="date"
                      className={styles.modalDateInput}
                      onChange={(e) => {
                        const v = e.target.value;
                        const blocked = !!modalActivity && isActivityDateBlocked(modalActivity, v);
                        if (v && !modalDates.includes(v) && !blocked) {
                          setModalDates(prev => [...prev, v]);
                        }
                        // Always reset the native picker (clears a rejected blocked pick too).
                        setDateInputKey(k => k + 1);
                      }}
                      min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                    />
                  </div>
                )}
              </div>

              {/* Guests section */}
              <div className={styles.modalSection}>
                <h4 className={styles.modalSectionTitle}>{t('guests')}</h4>
                <div className={styles.modalGuestsRow}>
                  <div className={styles.modalGuestsLabel}>
                    <span className={styles.modalGuestsTitle}>{t('adultsLabel')}</span>
                    {t('agesHint') && <span className={styles.modalGuestsHint}>{t('agesHint')}</span>}
                  </div>
                  <div className={styles.modalGuestsControls}>
                    <button
                      className={`${styles.modalGuestBtn} ${modalGuests <= 1 ? styles.modalGuestBtnDisabled : ''}`}
                      onClick={() => setModalGuests(g => Math.max(1, g - 1))}
                      disabled={modalGuests <= 1}
                      aria-label={t('decreaseGuests')}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14" strokeLinecap="round" />
                      </svg>
                    </button>
                    <span className={styles.modalGuestCount}>{modalGuests}</span>
                    <button
                      className={`${styles.modalGuestBtn} ${modalGuests >= 12 ? styles.modalGuestBtnDisabled : ''}`}
                      onClick={() => setModalGuests(g => Math.min(12, g + 1))}
                      disabled={modalGuests >= 12}
                      aria-label={t('increaseGuests')}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className={styles.modalDivider} />

              {/* Price summary */}
              <div className={styles.modalPriceSummary}>
                {modalDates.length > 0 ? (
                  <div className={styles.modalPriceBreakdown}>
                    <span className={styles.modalPriceCalc}>
                      {modalDates.length} {t('dates', { count: modalDates.length })} &times; {modalGuests} {t('guestsLabel', { count: modalGuests })} &times; ${modalActivity.basePrice.amount.toLocaleString()}
                    </span>
                    <span className={styles.modalPriceTotal}>${modalTotal.toLocaleString()}</span>
                  </div>
                ) : (
                  <span className={styles.modalPriceEmpty}>{t('selectDatesToContinue')}</span>
                )}
              </div>

              {/* Confirm button */}
              <button
                className={`${styles.modalConfirmBtn} ${modalDates.length === 0 ? styles.modalConfirmBtnDisabled : ''}`}
                onClick={confirmModal}
                disabled={modalDates.length === 0}
              >
                <span className={styles.modalConfirmLabel}>
                  {modalDates.length > 0 ? t('addToTrip', { total: modalTotal.toLocaleString() }) : t('selectDatesToContinue')}
                </span>
                {modalDates.length > 0 && (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
